import { isAlphabet } from "../string";
import { IBANCountries } from "./data";

export function ValidEmail(mail?: string): boolean {
  if (!mail) return false;
  const atSign = mail.lastIndexOf("@");
  if (atSign === -1) return false;
  const domain = mail.substring(atSign + 1);
  const domainSeparator = domain.lastIndexOf(".");
  const domainExtension = domain.substring(domainSeparator + 1);
  if (!/\w{2,3}/.test(domainExtension)) return false;
  const domainLabel = domain.substring(0, domainSeparator);
  if (domainLabel.length > 63) return false;
  let validDomain = false;
  if (
    /^[A-Za-z0-9.-]+$/.test(domainLabel) &&
    !/(^[.-]|[.-]$|\.\.)/.test(domainLabel) &&
    !RegExp(`\\d{${domainLabel.length}}`).test(domainLabel)
  ) {
    validDomain = true;
  }
  const name = mail.substring(0, atSign);
  let validName = false;
  if (/^[A-Za-z0-9!#$%&'*+-/=?^_`{|}~.]+$/.test(name) && !/(^\.|\.$|\.\.)/.test(name)) {
    validName = true;
  }
  return validDomain && validName;
}

export function ValidNationalId(id?: string): boolean {
  if (!id || id.length !== 10) return false;
  let sum = 0;
  for (let i = 0; i < id.length - 1; i += 1) {
    sum += parseInt(id[i], 10) * (id.length - i);
  }
  const lastId = parseInt(id[id.length - 1], 10);
  const r = sum % 11;
  return (r < 2 && lastId === r) || (r >= 2 && lastId === 11 - r);
}

export function ValidPostalCode(code?: string): boolean {
  return /^(?!(d)\1{3})[13-9]{4}[0-9]{6}$/.test(code || "");
}

export function ValidMobileNumber(number?: string): boolean {
  /** Shaparak doesn't accept phone numbers starting with +98 */
  // return RegExp(/^(\+98|0098|98|0)?9\d{9}$/).test(number || "");
  return /^09\d{9}$/.test(number || "");
}

export function ValidSheba(sheba?: string): boolean {
  if (
    !IBANCountries.some(
      (country) =>
        // Currently only Iran IBANs should be accepted
        country.code === "IR" && sheba?.startsWith(country.code) && sheba?.length === country.length
    )
  )
    return false;
  let val = `${sheba?.substring(4, sheba?.length)}${sheba?.substr(0, 4)}`;
  val = val.replace(/[A-Z]/g, (m: string) => String(m.charCodeAt(0) - "A".charCodeAt(0) + 10));
  return parseInt(String(BigInt(val) % BigInt(97)), 10) === 1;
}

export function ValidCardNumber(value: string): boolean {
  if (value.length !== 16) return false;
  // We might be missing some banks codes which will result in `false negatives`
  // if (!bankCardCodes.some((code) => value.startsWith(code))) return false;
  let sum = 0;
  for (let i = 1; i <= value.length; i += 1) {
    if (i % 2 === 1) {
      sum += ((parseInt(value[i - 1], 10) * 2 - 1) % 9) + 1;
    } else {
      sum += parseInt(value[i - 1], 10);
    }
  }
  if (sum % 10 !== 0) return false;
  return true;
}

export function ValidLocalPhoneNumber(number?: string): boolean {
  /** Shaparak doesn't accept phone numbers starting with +98 */
  // return RegExp(/^(\+98|0)?\d{10}$/).test(number || "");
  return /^0\d{10}$/.test(number || "");
}

export function ValidUrl(url: string): boolean {
  return /^(https?|ftp):\/\/(((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/.test(
    url
  );
}

export function ValidUrlOptionalProtocol(url: string): boolean {
  return /^((https?|ftp):\/\/)?(((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-zA-Z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)+(\/(([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)*)*)?)?(\?((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(#((([a-zA-Z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-fA-F]{2})|[!$&'()*+,;=]|:|@)|\/|\?)*)?$/.test(
    url
  );
}

export function ValidUrlWithLocalhost(url: string): boolean {
  return /(?:^|\s)((https?:\/\/)?(?:localhost|[\w-]+(?:\.[\w-]+)+)(:\d+)?(\/\S*)?)/.test(url);
}

export function ValidPersianName(name: string): boolean {
  return isAlphabet(name) && !/[\u0041-\u007a]/.test(name);
}

export function ValidPassword(pass: string): boolean {
  return /(?=.*[A-Z])(?=.*\d)[A-Za-z\d]*/.test(pass);
}
